import Typography from '@mui/material/Typography';
import React from 'react';
import { InputComponentProps, ValueComponentProps } from '../../../design-system';
import { getNestedHelperText, toChangeEvent } from '../../../utils/formUtils';
import { TextField } from '../../TextField';
import { IcpLookupInput } from './IcpLookupInput';
import type { IcpOption } from './InstallationPointOption';

export type InstallationPoint = {
  address?: string;
  countryShort?: string;
  id?: string;
};

type InstallationPointInputProps = InputComponentProps<InstallationPoint | null>;
export const InstallationPointInput: React.VFC<InstallationPointInputProps> = ({
  disabled,
  error,
  helperText,
  name,
  onBlur,
  onChange,
  value,
}: InstallationPointInputProps) => {
  const [queryParameters, setQueryParameters] = React.useState<Pick<InstallationPoint, 'address' | 'countryShort'>>(
    value ?? {},
  );
  const [selectedOption, setSelectedOption] = React.useState<IcpOption | null>(null);

  const onValueChanged = React.useCallback(
    (eventOrFieldName: React.ChangeEvent<ValueComponentProps<IcpOption | null>> | string) => {
      if (typeof eventOrFieldName === 'string') {
        return onChange(eventOrFieldName);
      }
      const {
        target: { value: newValue },
      } = eventOrFieldName;
      const event = {
        ...eventOrFieldName,
        target: { ...eventOrFieldName.target, value: { id: newValue?.id } },
      };

      return onChange(name)(event);
    },
    [name, onChange],
  );

  React.useEffect(() => {
    const { id, address, countryShort } = value ?? {};
    setSelectedOption(id ? (v) => ({ address: v?.address ?? '', id }) : null);

    if (address && countryShort) {
      setQueryParameters({ address, countryShort });
    }
  }, [value]);

  const { address, countryShort } = queryParameters;

  if (countryShort === 'NZ' && address) {
    return (
      <IcpLookupInput
        address={address}
        error={error}
        disabled={disabled}
        helperText={helperText}
        name={name}
        onBlur={onBlur}
        onChange={onValueChanged}
        value={selectedOption}
      />
    );
  }
  return (
    <TextField
      disabled={disabled}
      error={error}
      fullWidth
      helperText={getNestedHelperText(helperText)}
      name={name}
      onBlur={onBlur}
      onChange={(e) => {
        const transformedEvent = toChangeEvent<InstallationPoint | null>(
          e,
          e.target.value ? { id: e.target.value } : null,
        );
        onChange(name)(transformedEvent);
      }}
      value={selectedOption?.id ?? ''}
    />
  );
};

export const InstallationPointValue: React.VFC<ValueComponentProps<InstallationPoint | null>> = ({
  value,
}: ValueComponentProps<InstallationPoint | null>) => <Typography variant="body2">{value?.id}</Typography>;
